import { Button, Text } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { CrudSection } from '../../components/CrudSection';
import { type ShippingCharge, ShippingChargeForm } from './ShippingChargeForm';

const CHARGES_PATH = '/admin/v1/suppliers/freight-charges';

/** How a charge is named when it is being blamed for covering a delivery twice. */
function describe(charge: ShippingCharge): string {
  const who = charge.carrierName || 'another carrier';
  return charge.reference ? `${who} (${charge.reference})` : who;
}

/**
 * What it cost to get goods across, and which deliveries each bill paid for.
 *
 * The table is the generic one; recording and editing a charge is not, because a charge is a bill
 * AND the crossing it paid for, and the generic form has no way to ask "which deliveries?" — it
 * offers one value per field. So the button opens a purpose-built dialog instead, and the tab
 * finally does what its own description has always promised.
 */
export function ShippingChargesSection({ locationId }: { locationId?: string }) {
  const queryClient = useQueryClient();
  /** null = closed; { charge: null } = recording a new one. */
  const [editing, setEditing] = useState<{ charge: ShippingCharge | null } | null>(null);

  // Same key and same request as the table below, so this shares one fetch with it rather than
  // making a second. It is read for the one thing the table cannot work out on its own: whether a
  // delivery is already being charged for by somebody else.
  const charges = useQuery({
    queryKey: listKey(CHARGES_PATH),
    queryFn: () => rawApi<ShippingCharge[]>('GET', CHARGES_PATH),
  });

  const alreadyCovered = new Map<string, string>();
  for (const charge of charges.data ?? []) {
    if (charge.id === editing?.charge?.id) continue;
    for (const receiptId of charge.receiptIds ?? []) alreadyCovered.set(receiptId, describe(charge));
  }

  const refresh = () => void queryClient.invalidateQueries({ queryKey: listKey(CHARGES_PATH) });

  return (
    <>
      <CrudSection
        title="Shipping charges"
        entityNoun="shipping charge"
        description="What it cost to get goods from the mainland to the island. A mainland supplier usually drops the order at the ferry or a freight company, and it is they who bill you — so the charge belongs to the crossing, not to whoever sold you the goods. Record it here with the deliveries it covered and the cost is spread across them."
        listPath={CHARGES_PATH}
        emptyMessage="No shipping charges recorded. Add one when the ferry or freight company bills you."
        actions={
          <Button size="sm" onClick={() => setEditing({ charge: null })}>
            Add a shipping charge
          </Button>
        }
        columns={[
          { accessor: 'carrierName', title: 'Who brought it' },
          { accessor: 'reference', title: 'Their reference' },
          { accessor: 'amountMinor', title: 'They charged', unit: 'money' },
          { accessor: 'chargedAt', title: 'Date', unit: 'date' },
          {
            accessor: 'receiptIds',
            title: 'Covered',
            noWrap: true,
            render: (row) => {
              const count = Array.isArray(row.receiptIds) ? row.receiptIds.length : 0;
              // A charge covering nothing is not spread over any stock, so it is worth seeing at a
              // glance rather than discovering when a plate cost looks too low.
              return count === 0 ? (
                <Text size="sm" c="dimmed">
                  Nothing yet
                </Text>
              ) : (
                `${count} ${count === 1 ? 'delivery' : 'deliveries'}`
              );
            },
          },
          { accessor: 'allocationMethod', title: 'Split', enumGroup: 'landedCostAllocation' },
          { accessor: 'status', title: 'Status', enumGroup: 'freightChargeStatus' },
        ]}
        rowActions={(row) => (
          <Button
            size="xs"
            variant="light"
            onClick={() => setEditing({ charge: row as unknown as ShippingCharge })}
          >
            Edit
          </Button>
        )}
      />

      {editing && (
        <ShippingChargeForm
          key={editing.charge?.id ?? 'new'}
          charge={editing.charge}
          locationId={locationId}
          alreadyCovered={alreadyCovered}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
    </>
  );
}
